import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ShieldCheck, Sparkles, CheckCircle2, AlertCircle, Loader2,
  Upload, CameraIcon, IdCard, ArrowLeft,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getToken = () =>
  localStorage.getItem('user_token') || localStorage.getItem('token');

const DOC_TYPES = [
  { value: 'passport', label: 'Passport', hint: 'Clear photo of the information page.', requiresBack: false },
  { value: 'drivers_license', label: "Driver's licence", hint: 'Front AND back of the card. All four corners visible on each side.', requiresBack: true },
  { value: 'national_id', label: 'National ID card', hint: 'Front AND back of the card. All four corners visible on each side.', requiresBack: true },
];

/**
 * IdVerificationRequiredModal — manual admin-review flow.
 *
 * Replaces the Stripe Identity integration. User:
 *   1. Picks doc type
 *   2. Uploads ID photo
 *   3. Uploads selfie-with-ID
 *   4. Submits → status "pending" until admin approves
 *
 * If user already has a pending submission, we show the "Under review" screen
 * immediately. If rejected, the reason is shown and they can re-submit.
 */
const IdVerificationRequiredModal = ({ isOpen, onClose, onVerified }) => {
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [manualStatus, setManualStatus] = useState('not_started');
  const [rejectionReason, setRejectionReason] = useState(null);
  const [idVerified, setIdVerified] = useState(false);

  const [step, setStep] = useState('choose'); // choose | id | id_back | review | submitting
  const [docType, setDocType] = useState('passport');
  const [idFile, setIdFile] = useState(null);
  const [idBackFile, setIdBackFile] = useState(null);
  const [idPreview, setIdPreview] = useState(null);
  const [idBackPreview, setIdBackPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Whether the currently-selected doc type requires a back-of-card photo.
  const requiresBack = useMemo(
    () => DOC_TYPES.find((d) => d.value === docType)?.requiresBack === true,
    [docType],
  );

  // Pull current status when modal opens
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      setLoadingStatus(true);
      try {
        const r = await axios.get(`${API_BASE}/verification/status`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (cancelled) return;
        const d = r.data || {};
        setIdVerified(!!d.id_verified);
        setManualStatus(d.manual_status || 'not_started');
        setRejectionReason(d.manual_rejection_reason || null);
        if (d.id_verified) {
          onVerified?.();
          onClose();
          return;
        }
      } catch {
        if (!cancelled) setManualStatus('not_started');
      } finally {
        if (!cancelled) setLoadingStatus(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, onClose, onVerified]);

  const resetToEdit = useCallback(() => {
    setStep('choose');
    setIdFile(null); setIdBackFile(null);
    setIdPreview(null); setIdBackPreview(null);
  }, []);

  const handleFilePick = (which) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/^image\/(jpeg|png|webp)$/.test(f.type)) {
      toast({ title: 'Unsupported file', description: 'Use JPG, PNG, or WebP.', variant: 'destructive' });
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Max 10 MB.', variant: 'destructive' });
      return;
    }
    const url = URL.createObjectURL(f);
    if (which === 'id') { setIdFile(f); setIdPreview(url); }
    else if (which === 'id_back') { setIdBackFile(f); setIdBackPreview(url); }
  };

  const uploadOne = useCallback(async (file, kind) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    const r = await axios.post(`${API_BASE}/verification/upload-doc`, fd, {
      headers: {
        Authorization: `Bearer ${getToken()}`,
        'Content-Type': 'multipart/form-data',
      },
    });
    return r.data.path;
  }, []);

  const handleSubmit = async () => {
    if (!idFile) return;
    if (requiresBack && !idBackFile) return;
    setSubmitting(true);
    setStep('submitting');
    try {
      const idPath = await uploadOne(idFile, 'id');
      const idBackPath = idBackFile ? await uploadOne(idBackFile, 'id_back') : null;
      await axios.post(
        `${API_BASE}/verification/manual-submit`,
        {
          doc_type: docType,
          id_path: idPath,
          id_back_path: idBackPath,
        },
        { headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' } },
      );
      setManualStatus('pending');
      setRejectionReason(null);
      toast({
        title: 'Submitted ✓',
        description: 'Our team will review your ID within 1 business day.',
      });
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e.message
        || 'Submission failed';
      toast({ title: 'Could not submit', description: String(msg), variant: 'destructive' });
      setStep('review');
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = useMemo(
    () => !!idFile && (!requiresBack || !!idBackFile)
      && DOC_TYPES.some((d) => d.value === docType),
    [idFile, idBackFile, docType, requiresBack],
  );

  // ------- Render states -------

  const renderPending = () => (
    <div className="space-y-4 py-2" data-testid="id-verification-pending-state">
      <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 flex items-start gap-3">
        <Loader2 className="w-5 h-5 text-amber-700 animate-spin mt-0.5" />
        <div>
          <div className="font-semibold text-amber-900">Your ID is under review</div>
          <div className="text-sm text-amber-800 mt-0.5">
            An admin will review your documents within 1 business day. We'll
            email you as soon as a decision is made — no need to re-submit.
          </div>
        </div>
      </div>
    </div>
  );

  const renderRejected = () => (
    <div className="space-y-4 py-2" data-testid="id-verification-rejected-state">
      <div className="p-4 rounded-lg bg-red-50 border border-red-200">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-700 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-semibold text-red-900">We couldn't approve your previous submission</div>
            {rejectionReason && (
              <div className="text-sm text-red-800 mt-1 italic">"{rejectionReason}"</div>
            )}
            <div className="text-sm text-red-800 mt-2">
              Please upload a new photo of your ID.
            </div>
          </div>
        </div>
      </div>
      <Button
        onClick={() => { setManualStatus('not_started'); resetToEdit(); }}
        className="w-full bg-amber-600 hover:bg-amber-700 text-white"
        data-testid="id-verification-retry"
      >
        Upload new photos
      </Button>
    </div>
  );

  const renderChoose = () => (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-3 gap-3 text-center text-xs">
        <div className="p-3 rounded-lg bg-amber-50 border border-amber-100">
          <ShieldCheck className="w-6 h-6 mx-auto text-amber-600 mb-1" />
          <div className="font-semibold text-amber-900">Secure</div>
          <div className="text-amber-800">Encrypted. Auto-deleted after 90 days.</div>
        </div>
        <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-100">
          <Sparkles className="w-6 h-6 mx-auto text-emerald-600 mb-1" />
          <div className="font-semibold text-emerald-900">One-time</div>
          <div className="text-emerald-800">Verify once, book anywhere.</div>
        </div>
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
          <CheckCircle2 className="w-6 h-6 mx-auto text-blue-600 mb-1" />
          <div className="font-semibold text-blue-900">Admin-reviewed</div>
          <div className="text-blue-800">Usually within 1 business day.</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Which document will you upload?
        </label>
        <div className="space-y-2">
          {DOC_TYPES.map((d) => (
            <label
              key={d.value}
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition
                ${docType === d.value ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'}`}
              data-testid={`id-verification-doctype-${d.value}`}
            >
              <input
                type="radio"
                name="doc_type"
                value={d.value}
                checked={docType === d.value}
                onChange={() => setDocType(d.value)}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-900">{d.label}</div>
                <div className="text-xs text-gray-600">{d.hint}</div>
              </div>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  const FileSlot = ({ label, icon: Icon, preview, onPick, testId }) => (
    <div className="space-y-2">
      <div className="text-sm font-medium text-gray-700 flex items-center gap-2">
        <Icon className="w-4 h-4" /> {label}
      </div>
      <label className={`block rounded-lg border-2 border-dashed p-4 cursor-pointer transition
        ${preview ? 'border-emerald-400 bg-emerald-50/50' : 'border-gray-300 hover:border-amber-400 hover:bg-amber-50/30'}`}
        data-testid={testId}
      >
        {preview ? (
          <img src={preview} alt="preview" className="mx-auto max-h-40 rounded" />
        ) : (
          <div className="text-center py-6 text-gray-500 text-sm">
            <Upload className="w-6 h-6 mx-auto mb-2" />
            Click to upload · JPG / PNG / WebP · max 10 MB
          </div>
        )}
        <input type="file" accept="image/jpeg,image/png,image/webp"
          className="hidden" onChange={onPick}
        />
      </label>
    </div>
  );

  const renderIdStep = () => (
    <div className="space-y-4 py-2">
      <FileSlot
        label={requiresBack ? 'Front of your ID' : 'Photo of your ID'}
        icon={IdCard}
        preview={idPreview}
        onPick={handleFilePick('id')}
        testId="id-verification-upload-id"
      />
      <div className="text-xs text-gray-500">
        Tips: good lighting, no glare, all four corners visible. Your document
        must match the doc type you selected.
      </div>
    </div>
  );

  const renderIdBackStep = () => (
    <div className="space-y-4 py-2">
      <FileSlot
        label="Back of your ID"
        icon={IdCard}
        preview={idBackPreview}
        onPick={handleFilePick('id_back')}
        testId="id-verification-upload-id-back"
      />
      <div className="text-xs text-gray-500">
        Capture the reverse side of the same card. Make sure the magnetic stripe
        or barcode area is fully visible — that's where the issuing authority
        encodes the security data.
      </div>
    </div>
  );

  const renderReview = () => (
    <div className="space-y-4 py-2">
      <div className="p-4 rounded-lg border border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-700 space-y-1">
          <div><span className="text-gray-500">Document:</span> <strong>{DOC_TYPES.find((d) => d.value === docType)?.label}</strong></div>
          <div><span className="text-gray-500">ID front:</span> {idFile?.name}</div>
          {requiresBack && (
            <div><span className="text-gray-500">ID back:</span> {idBackFile?.name}</div>
          )}
        </div>
      </div>
      <div className={`grid ${requiresBack ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
        <img src={idPreview} alt="ID front" className="rounded border border-gray-200 max-h-40 object-contain mx-auto" />
        {requiresBack && (
          <img src={idBackPreview} alt="ID back" className="rounded border border-gray-200 max-h-40 object-contain mx-auto" />
        )}
      </div>
      <p className="text-xs text-gray-500">
        By submitting, you consent to Homesbyli storing these images securely
        until an admin reviews them. After approval, images are auto-deleted
        90 days later.
      </p>
    </div>
  );

  const renderSubmitting = () => (
    <div className="py-10 text-center">
      <Loader2 className="w-10 h-10 animate-spin text-amber-600 mx-auto mb-4" />
      <div className="font-semibold text-gray-900">Uploading…</div>
      <div className="text-sm text-gray-600">Hold tight — this takes a few seconds.</div>
    </div>
  );

  // ---- Decide what to render based on server status ----
  const showPending = !loadingStatus && manualStatus === 'pending';
  const showRejected = !loadingStatus && manualStatus === 'rejected';
  const showWizard = !loadingStatus && !showPending && !showRejected;

  // Footer buttons vary per step
  const renderFooter = () => {
    if (showPending) {
      return (
        <div className="flex justify-end">
          <Button onClick={onClose} data-testid="id-verification-close">Close</Button>
        </div>
      );
    }
    if (showRejected) {
      return (
        <div className="flex justify-end">
          <Button variant="outline" onClick={onClose} data-testid="id-verification-cancel">
            Close
          </Button>
        </div>
      );
    }
    if (step === 'submitting') return null;

    return (
      <div className="flex gap-2 justify-between">
        <div>
          {step !== 'choose' && (
            <Button variant="ghost" onClick={() => {
              if (step === 'review') setStep(requiresBack ? 'id_back' : 'id');
              else if (step === 'id_back') setStep('id');
              else if (step === 'id') setStep('choose');
            }} data-testid="id-verification-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} data-testid="id-verification-cancel">
            Not now
          </Button>
          {step === 'choose' && (
            <Button onClick={() => setStep('id')}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="id-verification-next">
              Continue
            </Button>
          )}
          {step === 'id' && (
            <Button
              onClick={() => setStep(requiresBack ? 'id_back' : 'review')}
              disabled={!idFile}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="id-verification-next"
            >Next</Button>
          )}
          {step === 'id_back' && (
            <Button
              onClick={() => setStep('review')}
              disabled={!idBackFile}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="id-verification-next"
            >Next</Button>
          )}
          {step === 'review' && (
            <Button
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="id-verification-submit"
            >
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
                : 'Submit for review'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="id-verification-required-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <ShieldCheck className="w-6 h-6 text-amber-600" />
            {idVerified
              ? "You're verified"
              : showPending
                ? 'Under review'
                : showRejected
                  ? 'ID verification needed'
                  : 'Verify your ID'}
          </DialogTitle>
          <DialogDescription className="text-base text-gray-700 pt-2">
            {showPending
              ? "We've received your documents. No action needed right now."
              : showRejected
                ? 'Your previous submission was not approved — please upload a fresh photo.'
                : 'Upload a clear photo of your government ID. Our team reviews within 1 business day.'}
          </DialogDescription>
        </DialogHeader>

        {loadingStatus && (
          <div className="py-10 text-center">
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-gray-400" />
          </div>
        )}

        {!loadingStatus && showPending && renderPending()}
        {!loadingStatus && showRejected && renderRejected()}
        {!loadingStatus && showWizard && step === 'choose' && renderChoose()}
        {!loadingStatus && showWizard && step === 'id' && renderIdStep()}
        {!loadingStatus && showWizard && step === 'id_back' && renderIdBackStep()}
        {!loadingStatus && showWizard && step === 'review' && renderReview()}
        {!loadingStatus && showWizard && step === 'submitting' && renderSubmitting()}

        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
};

export default IdVerificationRequiredModal;
